import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import SEO from "../components/SEO";
import { MaskLineScroll } from "../components/Reveal";
import SignaturePad from "../components/SignaturePad";
import { BRAND, TID } from "../lib/brand";
import { submitEstimate } from "../lib/api";

// Small analytics stub — respects "no PII" rule. Only sends event names.
const track = (name, meta = {}) => {
  const payload = { name, ...meta };
  if (typeof window !== "undefined") {
    console.info("[analytics]", payload);
    if (window.dataLayer) window.dataLayer.push({ event: name, ...meta });
  }
};

const PROJECT_TYPES = [
  {
    id: "collision_insurance",
    label: "Collision or Insurance Repair",
    body: "Accident damage, insurance claims, dents, damaged panels, paint matching, structural assessment, and daily-driven vehicles.",
    icon: "collision",
  },
  {
    id: "custom_jdm_specialty",
    label: "Custom, JDM or Specialty Project",
    body: "Custom paint, complete color changes, imported vehicles, JDM restoration, race and drift cars, specialty panels, fabrication, and refinishing.",
    icon: "custom",
  },
  {
    id: "unsure",
    label: "Not sure — help me choose",
    body: "Send photos and a short description. ProForm will help identify the right next step.",
    icon: "help",
  },
];

const CTA_BY_TYPE = {
  collision_insurance: "Send Damage Photos",
  custom_jdm_specialty: "Submit Project Details",
  unsure: "Ask ProForm",
};

const HEADING_BY_TYPE = {
  collision_insurance: { h: "Show us what happened.", body: "Upload clear photos of the damage and provide any insurance information you already have. ProForm will review the visible condition and help determine the next step." },
  custom_jdm_specialty: { h: "Tell us what you're building.", body: "Send photos of the vehicle, its current condition, and the result you want. Include modifications, imported components, existing parts, paint ideas, or project history." },
  unsure: { h: "Send photos. We'll help sort it out.", body: "You don't need to know the repair terminology. Upload clear photos and describe what happened or what you want to change." },
};

const CATEGORY_BADGE = {
  collision_insurance: "COLLISION / INSURANCE",
  custom_jdm_specialty: "CUSTOM / JDM / SPECIALTY",
  unsure: "NEEDS CLASSIFICATION",
};

const initial = {
  first_name: "", last_name: "", phone: "", email: "", preferred_contact: "text",
  project_type: "",
  vehicle_year: "", vehicle_make: "", vehicle_model: "", vin: "", drivable: true,
  work_type: "collision",
  // Collision-specific (legacy compact fields kept for backward compat)
  collision_date: "", insurance_company: "", claim_number: "", adjuster_info: "",
  // Custom-specific
  desired_outcome: "", prior_mods: "", parts_obtained: "",
  // Shared
  description: "", timing: "", video_link: "",
  consent: false, honeypot: "",

  // ---- Expanded insurance section (all optional) ----
  insurance_policyholder: "", insurance_policy_number: "", date_of_loss: "",
  claim_type: "", deductible_amount: "",
  coverage_confirmed: "", insurance_estimate_issued: "",
  // Adjuster
  adjuster_first_name: "", adjuster_last_name: "", adjuster_company: "",
  adjuster_phone: "", adjuster_extension: "", adjuster_email: "",
  adjuster_portal: "", adjuster_notes: "",
  // Agent
  agent_name: "", agent_agency: "", agent_phone: "", agent_email: "",
  // Other party
  other_insurance_company: "", other_claim_number: "",
  other_adjuster_name: "", other_adjuster_phone: "", other_adjuster_email: "",
  // Vehicle location
  vehicle_location_type: "", vehicle_location_address: "",
  vehicle_location_contact: "", vehicle_location_phone: "",
  tow_required: false, tow_company: "",
  // Communication permissions
  permission_voicemail: false, permission_sms: false, permission_email: false,
  best_time_to_contact: "",

  // ---- Preliminary Review Acknowledgment ----
  ack_agreed: false,
  ack_legal_name: "",
  ack_signature: null, // data URL of drawn signature (optional)
};

const INSURANCE_DOC_TYPES = [
  { id: "insurance_estimate", label: "Insurance estimate" },
  { id: "claim_acknowledgment", label: "Claim acknowledgment" },
  { id: "policy_declarations", label: "Policy declarations" },
  { id: "adjuster_correspondence", label: "Adjuster correspondence" },
  { id: "total_loss_notice", label: "Total-loss notice" },
  { id: "supplement_request", label: "Supplement request" },
  { id: "tow_invoice", label: "Tow invoice" },
  { id: "prior_repair_invoice", label: "Prior repair invoice" },
  { id: "police_report", label: "Police report" },
  { id: "registration", label: "Registration" },
  { id: "title", label: "Title (only if requested)" },
  { id: "other", label: "Other supporting document" },
];

const VEHICLE_LOCATIONS = [
  { id: "customer_residence", label: "Customer residence" },
  { id: "proform", label: "ProForm" },
  { id: "tow_yard", label: "Tow yard" },
  { id: "storage_facility", label: "Storage facility" },
  { id: "other_repair_facility", label: "Other repair facility" },
  { id: "insurance_inspection_location", label: "Insurance inspection location" },
  { id: "copart", label: "Copart" },
  { id: "iaa", label: "IAA" },
  { id: "other", label: "Other" },
];

// The exact Preliminary Review Acknowledgment text.
// Version stamp lives on both the frontend record + the backend audit trail.
const ACK_VERSION = "prelim-1.0";
const ACK_TEXT = `I understand that this submission is a request for a preliminary review based on the information, photographs, and documents I provide. It is not a final estimate, repair order, guarantee of price, guarantee of coverage, or authorization for ProForm Paint & Body to perform repairs.

I understand that photographs may not reveal hidden damage, structural damage, mechanical damage, prior repairs, corrosion, calibration needs, blending requirements, parts availability, or other conditions that may affect the repair plan and final cost.

I represent that the information I submit is accurate to the best of my knowledge and that I am the vehicle owner or am authorized by the vehicle owner to submit this request.

I authorize ProForm Paint & Body to contact me by the communication methods I selected regarding this request. I understand that insurance approval or payment is determined by the applicable insurance company and policy, not by ProForm Paint & Body.`;

const STEPS = ["Select project type", "Vehicle & photos", "Contact & submit"];

// ProjectTypeCard — accessible radio-like card
function ProjectTypeCard({ opt, selected, onSelect }) {
  const active = selected === opt.id;
  return (
    <button
      type="button"
      role="radio"
      aria-checked={active}
      aria-label={opt.label}
      data-testid={`ptype-card-${opt.id}`}
      onClick={() => onSelect(opt.id)}
      className={`group relative text-left w-full min-h-[130px] md:min-h-[220px] p-6 md:p-7 flex flex-col border transition-all duration-300
        ${active
          ? "border-proform-red bg-[rgba(242,27,35,0.06)]"
          : "border-ink-700 bg-ink-900 hover:bg-ink-800 hover:border-chrome-300/50"}`}
    >
      <div className="flex items-start justify-between">
        <div className={`w-10 h-10 flex items-center justify-center border ${active ? "border-proform-red text-proform-red" : "border-ink-600 text-chrome-300 group-hover:border-chrome-300 group-hover:text-chrome-100"} transition-colors`}>
          <IconFor kind={opt.icon} />
        </div>
        {active && (
          <span className="inline-flex items-center gap-1.5 font-mono text-[10px] tracking-[0.24em] text-proform-red">
            <span className="w-1.5 h-1.5 rounded-full bg-proform-red" /> SELECTED
          </span>
        )}
      </div>
      <div className="mt-6 font-display uppercase text-xl md:text-2xl leading-tight tracking-tight text-chrome-100">
        {opt.label}
      </div>
      <p className="mt-3 text-chrome-300 text-sm leading-relaxed">{opt.body}</p>
      <div className="mt-auto pt-6 flex items-center justify-between">
        <span className={`h-px transition-all duration-300 ${active ? "w-14 bg-proform-red" : "w-8 bg-chrome-100/25 group-hover:w-12 group-hover:bg-chrome-300"}`} />
        <span className={`font-mono text-[11px] tracking-[0.24em] transition-colors ${active ? "text-proform-red" : "text-chrome-300 group-hover:text-chrome-100"}`}>
          {active ? "✓ CHOSEN" : "SELECT →"}
        </span>
      </div>
    </button>
  );
}

function IconFor({ kind }) {
  const p = "M";
  const stroke = "currentColor";
  if (kind === "collision") {
    return (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth="1.5" aria-hidden>
        <path d="M3 12h4l2-3h6l2 3h4v6h-3a2 2 0 1 1-4 0H10a2 2 0 1 1-4 0H3v-6Z" />
        <path d="M13.5 5.5l1 2 2 .5-1.5 1.4.4 2.1-1.9-1-1.9 1 .4-2.1L10.5 8l2-.5 1-2Z" />
      </svg>
    );
  }
  if (kind === "custom") {
    return (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth="1.5" aria-hidden>
        <path d="M4 15c0-3 4-6 8-6s8 3 8 6" />
        <path d="M4 15h16v3H4z" />
        <circle cx="8" cy="18" r="1.5" />
        <circle cx="16" cy="18" r="1.5" />
        <path d="M12 3v4" />
      </svg>
    );
  }
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth="1.5" aria-hidden>
      <circle cx="12" cy="12" r="9" />
      <path d="M9.5 9.5a2.5 2.5 0 1 1 3.5 2.3c-.9.4-1.5 1-1.5 2v.2" />
      <circle cx="12" cy="17" r="0.8" fill={stroke} />
    </svg>
  );
}

// Categorized insurance-document uploader — sits inside the Collision path in step 1.
function InsuranceDocsUploader({ items, setItems }) {
  const inputRef = React.useRef(null);
  const [pickingCategory, setPickingCategory] = useState("insurance_estimate");

  const pick = () => inputRef.current?.click();
  const onChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setItems((prev) => [
      ...prev,
      ...files.map((file) => ({ file, category: pickingCategory })),
    ]);
    if (inputRef.current) inputRef.current.value = "";
  };
  const remove = (idx) => setItems((prev) => prev.filter((_, i) => i !== idx));
  const setCategory = (idx, category) =>
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, category } : it)));

  return (
    <div className="space-y-3">
      <div className="flex flex-col sm:flex-row gap-2">
        <select
          className="field sm:w-72"
          value={pickingCategory}
          onChange={(e) => setPickingCategory(e.target.value)}
          data-testid="insurance-doc-category"
        >
          {INSURANCE_DOC_TYPES.map((t) => (
            <option key={t.id} value={t.id}>{t.label}</option>
          ))}
        </select>
        <input
          ref={inputRef}
          type="file"
          accept="application/pdf,image/jpeg,image/png,image/webp,image/heic,image/heif"
          multiple
          onChange={onChange}
          className="hidden"
          data-testid="insurance-doc-input"
        />
        <button
          type="button"
          onClick={pick}
          className="btn justify-center"
          data-testid="insurance-doc-add"
        >
          + Add document(s) as this category
        </button>
      </div>

      {items.length > 0 && (
        <ul className="space-y-2" data-testid="insurance-doc-list">
          {items.map((it, i) => (
            <li key={i} className="flex items-center gap-3 border border-ink-700 p-3">
              <div className="flex-1 min-w-0">
                <div className="text-chrome-100 text-sm truncate">{it.file.name}</div>
                <div className="label">{(it.file.size / 1024).toFixed(0)} KB</div>
              </div>
              <select
                className="field py-1 text-xs w-56"
                value={it.category}
                onChange={(e) => setCategory(i, e.target.value)}
              >
                {INSURANCE_DOC_TYPES.map((t) => (
                  <option key={t.id} value={t.id}>{t.label}</option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => remove(i)}
                className="label hover:text-proform-red"
                aria-label={`Remove ${it.file.name}`}
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function Estimate() {
  const [step, setStep] = useState(0);
  const [data, setData] = useState(initial);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [files, setFiles] = useState([]);
  const [insuranceFiles, setInsuranceFiles] = useState([]); // [{file, category}]
  const [busy, setBusy] = useState(false);
  const [reference, setReference] = useState(null);
  const [confirmation, setConfirmation] = useState(null);
  const [error, setError] = useState("");

  // For collision/insurance jobs the insurance details block is critical — auto-open it
  // so customers don't have to discover the "+ Show" toggle.
  useEffect(() => {
    if (data.project_type === "collision_insurance") setShowAdvanced(true);
  }, [data.project_type]);

  const set = (k) => (e) => {
    const v = e?.target?.type === "checkbox" ? e.target.checked : e.target.value;
    setData((d) => ({ ...d, [k]: v }));
  };
  const setBool = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const onSelectType = (id) => {
    setData((d) => ({ ...d, project_type: id }));
    track("project_selector_choice", { project_type: id });
    // Auto-advance to step 1 after 300ms for a clean UX
    window.setTimeout(() => setStep(1), 300);
  };

  const canContinue = useMemo(() => {
    if (step === 0) return !!data.project_type;
    if (step === 1) return data.vehicle_year && data.vehicle_make && data.vehicle_model && data.description.length > 10;
    return (
      data.consent &&
      data.ack_agreed &&
      (data.ack_legal_name || "").trim().length >= 3 &&
      data.first_name && data.last_name && data.phone && data.email
    );
  }, [step, data]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!data.consent) { setError("Please accept the contact consent checkbox."); return; }
    if (!data.ack_agreed || !(data.ack_legal_name || "").trim()) {
      setError("Please complete the Preliminary Review Acknowledgment (check the box and type your full legal name).");
      return;
    }
    setBusy(true);
    track("estimate_form_completed", {
      project_type: data.project_type,
      files: files.length,
      insurance_files: insuranceFiles.length,
    });
    try {
      const workTypeMap = { collision_insurance: "collision", custom_jdm_specialty: "custom_paint", unsure: "other" };
      // Extract acknowledgment fields into a nested object matching backend model.
      const {
        ack_agreed, ack_legal_name, ack_signature,
        ...rest
      } = data;
      // Empty strings on Literal-typed optional fields break Pydantic — coerce to null.
      // (project_type, claim_type, coverage_confirmed, insurance_estimate_issued, preferred_contact, drivable)
      const LITERAL_FIELDS = [
        "project_type", "claim_type", "coverage_confirmed", "insurance_estimate_issued",
      ];
      const cleaned = { ...rest };
      for (const k of LITERAL_FIELDS) {
        if (cleaned[k] === "") cleaned[k] = null;
      }
      const payload = {
        ...cleaned,
        work_type: cleaned.work_type || workTypeMap[cleaned.project_type] || "other",
        acknowledgment: {
          agreed: ack_agreed,
          legal_name: ack_legal_name.trim(),
          signature_data_url: ack_signature || null,
          version: ACK_VERSION,
        },
      };
      const res = await submitEstimate(payload, files, insuranceFiles);
      setReference(res.reference);
      setConfirmation(res.project_type || data.project_type);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      // Normalize the backend error into a friendly message.
      const raw = err?.response?.data?.detail;
      let friendly = "Something went wrong submitting your request. Please try again or call 720-839-8046.";
      if (typeof raw === "string") {
        // Pydantic dumps look like "Invalid estimate payload: N validation errors for EstimateIn\nfield\n  ..."
        // Show the user a clean, single-line message.
        if (raw.startsWith("Invalid estimate payload")) {
          const fields = [...raw.matchAll(/\n([a-z_]+)\n/g)].map(m => m[1]);
          friendly = fields.length
            ? `Please review these fields: ${[...new Set(fields)].join(", ")}. If the problem persists, call 720-839-8046.`
            : raw;
        } else {
          friendly = raw;
        }
      } else if (Array.isArray(raw)) {
        friendly = raw.map(e => e.msg || JSON.stringify(e)).join("; ");
      }
      setError(friendly);
      // Scroll the error banner (at the top of the form) into view so it's not off-screen.
      setTimeout(() => {
        const el = document.querySelector('[data-testid="estimate-form"]');
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 50);
    } finally {
      setBusy(false);
    }
  };

  const onFiles = (e) => {
    const arr = Array.from(e.target.files || []).slice(0, 12);
    setFiles(arr);
    if (arr.length > 0) track("first_photo_uploaded", { count: arr.length });
  };
  const removeFile = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));

  const changeType = () => setStep(0);

  // Success screen — category-aware
  if (reference) {
    const CONFIRM_COPY = {
      collision_insurance: "ProForm received your damage photos and vehicle details. The visible condition will be reviewed to determine whether additional photos or an in-person inspection are needed.",
      custom_jdm_specialty: "ProForm received your project details and photos. The scope will be reviewed for project fit, required inspection, parts needs, and next-step planning.",
      unsure: "ProForm received your photos and description. The shop will review the information and help identify the appropriate next step.",
    };
    const ptype = confirmation || "unsure";
    return (
      <>
        <SEO title="Request received — ProForm" description="Your ProForm inquiry has been received." />
        <section className="pt-40 pb-32">
          <div className="wrap max-w-2xl mx-auto text-center" data-testid={TID.estimate.success}>
            <div className="label mb-4">— Confirmation · {CATEGORY_BADGE[ptype]}</div>
            <h1 className="font-display uppercase text-5xl md:text-7xl leading-[0.9] tracking-tight">
              Request <span className="text-proform-red">received</span>.
            </h1>
            <div className="mt-8 card p-8">
              <div className="label">Your reference</div>
              <div data-testid={TID.estimate.reference} className="font-display text-4xl md:text-5xl text-proform-red mt-2">{reference}</div>
              <p className="mt-6 text-chrome-300 leading-relaxed">{CONFIRM_COPY[ptype]}</p>
              <p className="mt-4 text-chrome-300">
                For anything urgent, call or text
                <a href={`tel:${BRAND.phoneRaw}`} className="text-proform-red hover:underline"> {BRAND.phoneDisplay}</a>.
              </p>
            </div>
            <div className="mt-10 flex flex-wrap gap-3 justify-center">
              <a href={`tel:${BRAND.phoneRaw}`} className="btn">Call {BRAND.phoneDisplay}</a>
              <a href={`sms:${BRAND.phoneRaw}`} className="btn">Text ProForm</a>
              <Link to="/" className="btn">Back home</Link>
              <Link to="/services" className="btn">Explore services</Link>
            </div>
          </div>
        </section>
      </>
    );
  }

  const ptype = data.project_type;
  const heading = HEADING_BY_TYPE[ptype] || { h: "Send us the details.", body: "Choose a project type, then upload a few photos and describe what you need." };
  const submitCta = CTA_BY_TYPE[ptype] || "Send to ProForm";

  return (
    <>
      <SEO
        title="Upload Photos & Request an Estimate — ProForm Paint & Body Denver"
        description="Send collision, insurance, custom paint, or JDM project details to Cody at ProForm. Upload photos, describe the work, and get a preliminary review. Final pricing may require inspection."
        canonical={`https://${BRAND.domain}/estimate`}
      />

      <section className="pt-32 md:pt-40 pb-8 border-b border-ink-700">
        <div className="wrap grid md:grid-cols-12 gap-10">
          <div className="md:col-span-7">
            <div className="label mb-3">— Start with photos</div>
            <h1 className="font-display uppercase text-5xl md:text-7xl leading-[0.9] tracking-tight">
              <MaskLineScroll>Upload vehicle photos.</MaskLineScroll>
              <MaskLineScroll delay={0.06}>Get a <span className="text-proform-red">preliminary review</span>.</MaskLineScroll>
            </h1>
            <p className="mt-6 text-chrome-300 max-w-xl leading-relaxed">
              Photo reviews are preliminary. Hidden damage, disassembly, structural conditions, paint blending, parts availability, previous repairs, and insurance requirements may affect the final repair plan and price.
            </p>
          </div>
          <div className="md:col-span-5 md:pt-8">
            <div className="card p-6 md:p-8">
              <div className="label mb-3">Photo checklist</div>
              <ul className="text-chrome-100 text-sm space-y-2">
                <li>· Full front · full rear · both sides</li>
                <li>· Close-up damage · wider damage context</li>
                <li>· Wheel or suspension area if affected</li>
                <li>· Interior damage if applicable</li>
                <li>· Existing custom parts &amp; paint condition</li>
              </ul>
              <div className="label mt-4 text-chrome-300 normal-case tracking-wider">Phone photos are fine. Good lighting and multiple angles help.</div>
            </div>
          </div>
        </div>
      </section>

      {/* 3-step progress */}
      <div className="wrap py-8">
        <div className="grid grid-cols-3 gap-2" role="progressbar" aria-valuenow={step + 1} aria-valuemin={1} aria-valuemax={3}>
          {STEPS.map((s, i) => (
            <div key={s} className={`h-1 ${i <= step ? "bg-proform-red" : "bg-ink-700"} transition-colors`} />
          ))}
        </div>
        <div className="mt-3 flex flex-wrap gap-x-6 gap-y-2 label">
          {STEPS.map((s, i) => (
            <div key={s} className={`${i === step ? "text-proform-red" : ""}`}>0{i + 1} · {s}</div>
          ))}
        </div>
      </div>

      <form onSubmit={submit} data-testid={TID.estimate.form} className="wrap pb-16 md:pb-24">
        {error && <div className="mb-6 card p-4 border-proform-red text-proform-red text-sm" role="alert">{error}</div>}

        {/* Honeypot */}
        <input type="text" tabIndex={-1} autoComplete="off" name="honeypot" value={data.honeypot} onChange={set("honeypot")}
               className="absolute -left-[9999px]" aria-hidden />

        <AnimatePresence mode="wait">
          {/* STEP 0 — Project type selector */}
          {step === 0 && (
            <motion.div key="s0" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}
                        transition={{ duration: 0.35 }}>
              <div className="max-w-3xl">
                <h2 className="font-display uppercase text-3xl md:text-5xl leading-[0.95] tracking-tight">
                  What do you need help with?
                </h2>
                <p className="mt-3 text-chrome-300">Choose the option that best fits your vehicle. You can change it before submitting.</p>
              </div>

              <div
                role="radiogroup"
                aria-label="Project type"
                data-testid="project-type-selector"
                className="mt-8 md:mt-10 grid grid-cols-1 md:grid-cols-3 gap-4"
              >
                {PROJECT_TYPES.map((opt) => (
                  <ProjectTypeCard key={opt.id} opt={opt} selected={data.project_type} onSelect={onSelectType} />
                ))}
              </div>

              <p className="mt-6 label">Photo reviews are preliminary. An in-person inspection may be required before final pricing is issued.</p>
            </motion.div>
          )}

          {/* STEP 1 — Vehicle & photos */}
          {step === 1 && (
            <motion.div key="s1" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}
                        transition={{ duration: 0.35 }} className="space-y-8">

              {/* Category badge with change-type link */}
              <div className="flex flex-wrap items-center gap-3">
                <span className="inline-flex items-center gap-2 border border-proform-red text-proform-red font-mono text-[11px] tracking-[0.24em] px-3 py-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-proform-red" /> {CATEGORY_BADGE[ptype]}
                </span>
                <button type="button" onClick={changeType} className="font-mono text-[11px] tracking-[0.24em] text-chrome-300 hover:text-proform-red underline underline-offset-4">
                  Change project type
                </button>
              </div>

              <div>
                <h2 className="font-display uppercase text-3xl md:text-5xl leading-[0.95] tracking-tight">{heading.h}</h2>
                <p className="mt-3 text-chrome-300 max-w-2xl">{heading.body}</p>
              </div>

              {/* Vehicle */}
              <div className="grid md:grid-cols-3 gap-6">
                <div>
                  <label className="field-label" htmlFor="vy">Year *</label>
                  <input id="vy" required data-testid="estimate-year" className="field" value={data.vehicle_year} onChange={set("vehicle_year")} />
                </div>
                <div>
                  <label className="field-label" htmlFor="vm">Make *</label>
                  <input id="vm" required data-testid="estimate-make" className="field" value={data.vehicle_make} onChange={set("vehicle_make")} />
                </div>
                <div>
                  <label className="field-label" htmlFor="vmd">Model *</label>
                  <input id="vmd" required data-testid="estimate-model" className="field" value={data.vehicle_model} onChange={set("vehicle_model")} />
                </div>
                <div>
                  <label className="field-label">Drivable?</label>
                  <div className="grid grid-cols-2 gap-3">
                    <button type="button" onClick={() => setBool("drivable", true)}
                            className={`btn justify-center ${data.drivable ? "border-proform-red text-proform-red" : ""}`}>YES</button>
                    <button type="button" onClick={() => setBool("drivable", false)}
                            className={`btn justify-center ${!data.drivable ? "border-proform-red text-proform-red" : ""}`}>NO</button>
                  </div>
                </div>
                <div className="md:col-span-2">
                  <label className="field-label" htmlFor="desc">
                    {ptype === "custom_jdm_specialty" ? "What are you building? *" : ptype === "collision_insurance" ? "What happened? *" : "Describe what you need *"}
                  </label>
                  <textarea id="desc" required rows={4} data-testid="estimate-description" className="field" value={data.description} onChange={set("description")} placeholder="A short description helps — vehicle history, damage location, or the finish you want." />
                </div>
              </div>

              {/* Photo upload — prominent */}
              <div className="card p-6 md:p-8 border-proform-red/40">
                <label className="field-label text-proform-red" htmlFor="files">Upload photos for a preliminary repair assessment *</label>
                <p className="text-chrome-300 text-sm mt-1 mb-3">Send clear photos of the vehicle and damaged area. Multiple angles help.</p>
                <input
                  id="files"
                  type="file"
                  accept="image/*"
                  capture="environment"
                  multiple
                  onChange={onFiles}
                  data-testid={TID.estimate.fileInput}
                  className="field cursor-pointer"
                />
                {files.length > 0 && (
                  <div className="mt-4 grid grid-cols-3 md:grid-cols-6 gap-2">
                    {files.map((f, i) => (
                      <div key={`${f.name}-${i}`} className="relative clip-frame aspect-square group">
                        <img src={URL.createObjectURL(f)} alt={f.name} />
                        <button type="button" onClick={() => removeFile(i)}
                                aria-label={`Remove ${f.name}`}
                                className="absolute top-1 right-1 h-6 w-6 flex items-center justify-center bg-ink-950/85 border border-ink-700 text-chrome-100 hover:text-proform-red hover:border-proform-red font-mono text-[11px]">
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="label mt-3 normal-case tracking-wider text-chrome-300">Phone photos are fine. Good lighting and multiple angles help. Up to 12 photos · 12 MB each. JPG, PNG, WebP, HEIC.</div>
              </div>

              {/* Progressive disclosure: insurance / additional details */}
              <div className="border-t border-ink-700 pt-6">
                <button
                  type="button"
                  onClick={() => setShowAdvanced((v) => !v)}
                  className="w-full flex items-center justify-between text-left font-mono text-[11px] tracking-[0.24em] uppercase text-chrome-300 hover:text-proform-red"
                  aria-expanded={showAdvanced}
                >
                  <span>
                    {ptype === "collision_insurance" ? "Insurance details (optional — helps ProForm start work faster)" : ptype === "custom_jdm_specialty" ? "Project details, parts & timing" : "Additional details (optional)"}
                  </span>
                  <span>{showAdvanced ? "− Hide" : "+ Show"}</span>
                </button>

                <AnimatePresence>
                  {showAdvanced && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <div className="grid md:grid-cols-2 gap-6 pt-6">
                        <div>
                          <label className="field-label" htmlFor="vin">VIN (optional)</label>
                          <input id="vin" className="field" value={data.vin} onChange={set("vin")} />
                        </div>
                        <div>
                          <label className="field-label" htmlFor="tim">Preferred timing</label>
                          <input id="tim" className="field" placeholder="ASAP · this month · flexible…" value={data.timing} onChange={set("timing")} />
                        </div>

                        {ptype === "collision_insurance" && (
                          <div className="md:col-span-2 space-y-6">
                            <div className="border-l-2 border-proform-red pl-4">
                              <p className="text-chrome-300 text-sm leading-relaxed">
                                <strong className="text-chrome-100">Optional insurance details.</strong> You may submit the form without insurance information. Add whatever you currently have — ProForm can request the remaining details later.
                              </p>
                            </div>

                            {/* Insurance information */}
                            <fieldset className="border border-ink-700 p-5">
                              <legend className="label px-2">Insurance Information</legend>
                              <div className="grid md:grid-cols-2 gap-4">
                                <div>
                                  <label className="field-label">Insurance company</label>
                                  <input className="field" value={data.insurance_company} onChange={set("insurance_company")} data-testid="ins-company" />
                                </div>
                                <div>
                                  <label className="field-label">Policyholder name</label>
                                  <input className="field" value={data.insurance_policyholder} onChange={set("insurance_policyholder")} />
                                </div>
                                <div>
                                  <label className="field-label">Policy number</label>
                                  <input className="field" value={data.insurance_policy_number} onChange={set("insurance_policy_number")} />
                                </div>
                                <div>
                                  <label className="field-label">Claim number</label>
                                  <input className="field" value={data.claim_number} onChange={set("claim_number")} data-testid="ins-claim-number" />
                                </div>
                                <div>
                                  <label className="field-label">Date of loss</label>
                                  <input type="date" className="field" value={data.date_of_loss} onChange={set("date_of_loss")} />
                                </div>
                                <div>
                                  <label className="field-label">Deductible amount</label>
                                  <input className="field" placeholder="$500" value={data.deductible_amount} onChange={set("deductible_amount")} />
                                </div>
                                <div>
                                  <label className="field-label">Type of claim</label>
                                  <select className="field" value={data.claim_type} onChange={set("claim_type")}>
                                    <option value="">Choose one</option>
                                    <option value="own">Customer&apos;s own insurance</option>
                                    <option value="other_party">Other driver&apos;s insurance</option>
                                    <option value="unsure">Unsure</option>
                                  </select>
                                </div>
                                <div>
                                  <label className="field-label">Coverage confirmed?</label>
                                  <select className="field" value={data.coverage_confirmed} onChange={set("coverage_confirmed")}>
                                    <option value="">Choose one</option>
                                    <option value="yes">Yes</option>
                                    <option value="no">No</option>
                                    <option value="unsure">Unsure</option>
                                  </select>
                                </div>
                                <div>
                                  <label className="field-label">Insurance estimate issued?</label>
                                  <select className="field" value={data.insurance_estimate_issued} onChange={set("insurance_estimate_issued")}>
                                    <option value="">Choose one</option>
                                    <option value="yes">Yes</option>
                                    <option value="no">No</option>
                                    <option value="unsure">Unsure</option>
                                  </select>
                                </div>
                              </div>
                            </fieldset>

                            {/* Adjuster information */}
                            <fieldset className="border border-ink-700 p-5">
                              <legend className="label px-2">Adjuster Information</legend>
                              <div className="grid md:grid-cols-2 gap-4">
                                <div>
                                  <label className="field-label">First name</label>
                                  <input className="field" value={data.adjuster_first_name} onChange={set("adjuster_first_name")} />
                                </div>
                                <div>
                                  <label className="field-label">Last name</label>
                                  <input className="field" value={data.adjuster_last_name} onChange={set("adjuster_last_name")} />
                                </div>
                                <div>
                                  <label className="field-label">Adjuster company</label>
                                  <input className="field" value={data.adjuster_company} onChange={set("adjuster_company")} />
                                </div>
                                <div>
                                  <label className="field-label">Phone</label>
                                  <input type="tel" className="field" value={data.adjuster_phone} onChange={set("adjuster_phone")} />
                                </div>
                                <div>
                                  <label className="field-label">Extension</label>
                                  <input className="field" value={data.adjuster_extension} onChange={set("adjuster_extension")} />
                                </div>
                                <div>
                                  <label className="field-label">Email</label>
                                  <input type="email" className="field" value={data.adjuster_email} onChange={set("adjuster_email")} />
                                </div>
                                <div className="md:col-span-2">
                                  <label className="field-label">Adjuster claim portal / upload link</label>
                                  <input className="field" value={data.adjuster_portal} onChange={set("adjuster_portal")} />
                                </div>
                                <div className="md:col-span-2">
                                  <label className="field-label">Adjuster notes</label>
                                  <textarea rows={2} className="field" value={data.adjuster_notes} onChange={set("adjuster_notes")} />
                                </div>
                              </div>
                            </fieldset>

                            {/* Agent information */}
                            <fieldset className="border border-ink-700 p-5">
                              <legend className="label px-2">Insurance Agent (optional)</legend>
                              <div className="grid md:grid-cols-2 gap-4">
                                <div>
                                  <label className="field-label">Agent name</label>
                                  <input className="field" value={data.agent_name} onChange={set("agent_name")} />
                                </div>
                                <div>
                                  <label className="field-label">Agency</label>
                                  <input className="field" value={data.agent_agency} onChange={set("agent_agency")} />
                                </div>
                                <div>
                                  <label className="field-label">Phone</label>
                                  <input type="tel" className="field" value={data.agent_phone} onChange={set("agent_phone")} />
                                </div>
                                <div>
                                  <label className="field-label">Email</label>
                                  <input type="email" className="field" value={data.agent_email} onChange={set("agent_email")} />
                                </div>
                              </div>
                            </fieldset>

                            {/* Other party */}
                            {data.claim_type === "other_party" && (
                              <fieldset className="border border-ink-700 p-5">
                                <legend className="label px-2">Other Party Information</legend>
                                <div className="grid md:grid-cols-2 gap-4">
                                  <div>
                                    <label className="field-label">Other driver&apos;s insurance company</label>
                                    <input className="field" value={data.other_insurance_company} onChange={set("other_insurance_company")} />
                                  </div>
                                  <div>
                                    <label className="field-label">Other claim number</label>
                                    <input className="field" value={data.other_claim_number} onChange={set("other_claim_number")} />
                                  </div>
                                  <div>
                                    <label className="field-label">Other adjuster name</label>
                                    <input className="field" value={data.other_adjuster_name} onChange={set("other_adjuster_name")} />
                                  </div>
                                  <div>
                                    <label className="field-label">Other adjuster phone</label>
                                    <input type="tel" className="field" value={data.other_adjuster_phone} onChange={set("other_adjuster_phone")} />
                                  </div>
                                  <div className="md:col-span-2">
                                    <label className="field-label">Other adjuster email</label>
                                    <input type="email" className="field" value={data.other_adjuster_email} onChange={set("other_adjuster_email")} />
                                  </div>
                                </div>
                              </fieldset>
                            )}

                            {/* Vehicle location */}
                            <fieldset className="border border-ink-700 p-5">
                              <legend className="label px-2">Vehicle Location</legend>
                              <div className="grid md:grid-cols-2 gap-4">
                                <div>
                                  <label className="field-label">Where is the vehicle now?</label>
                                  <select className="field" value={data.vehicle_location_type} onChange={set("vehicle_location_type")}>
                                    <option value="">Choose one</option>
                                    {VEHICLE_LOCATIONS.map((l) => <option key={l.id} value={l.id}>{l.label}</option>)}
                                  </select>
                                </div>
                                <div>
                                  <label className="field-label">Contact person at location</label>
                                  <input className="field" value={data.vehicle_location_contact} onChange={set("vehicle_location_contact")} />
                                </div>
                                <div className="md:col-span-2">
                                  <label className="field-label">Address</label>
                                  <input className="field" value={data.vehicle_location_address} onChange={set("vehicle_location_address")} />
                                </div>
                                <div>
                                  <label className="field-label">Contact phone</label>
                                  <input type="tel" className="field" value={data.vehicle_location_phone} onChange={set("vehicle_location_phone")} />
                                </div>
                                <div>
                                  <label className="field-label">Tow required?</label>
                                  <div className="flex items-center gap-3 mt-2">
                                    <label className="flex items-center gap-2 text-sm text-chrome-300">
                                      <input type="checkbox" checked={data.tow_required} onChange={(e) => setBool("tow_required", e.target.checked)} className="accent-proform-red" />
                                      Tow needed
                                    </label>
                                    <input className="field flex-1" placeholder="Tow company (if known)" value={data.tow_company} onChange={set("tow_company")} />
                                  </div>
                                </div>
                              </div>
                            </fieldset>

                            {/* Communication preferences */}
                            <fieldset className="border border-ink-700 p-5">
                              <legend className="label px-2">Communication Preferences</legend>
                              <div className="grid md:grid-cols-2 gap-4">
                                <label className="flex items-start gap-3 cursor-pointer">
                                  <input type="checkbox" checked={data.permission_voicemail} onChange={(e) => setBool("permission_voicemail", e.target.checked)} className="mt-1 h-5 w-5 accent-proform-red" />
                                  <span className="text-chrome-300 text-sm">Permission to leave a voicemail</span>
                                </label>
                                <label className="flex items-start gap-3 cursor-pointer">
                                  <input type="checkbox" checked={data.permission_sms} onChange={(e) => setBool("permission_sms", e.target.checked)} className="mt-1 h-5 w-5 accent-proform-red" />
                                  <span className="text-chrome-300 text-sm">Permission to send SMS updates</span>
                                </label>
                                <label className="flex items-start gap-3 cursor-pointer">
                                  <input type="checkbox" checked={data.permission_email} onChange={(e) => setBool("permission_email", e.target.checked)} className="mt-1 h-5 w-5 accent-proform-red" />
                                  <span className="text-chrome-300 text-sm">Permission to email documents</span>
                                </label>
                                <div>
                                  <label className="field-label">Best time to contact</label>
                                  <input className="field" placeholder="Mornings, evenings, weekends…" value={data.best_time_to_contact} onChange={set("best_time_to_contact")} />
                                </div>
                              </div>
                            </fieldset>

                            {/* Insurance document upload (categorized) */}
                            <fieldset className="border border-ink-700 p-5">
                              <legend className="label px-2">Insurance Documents (optional)</legend>
                              <p className="text-chrome-300 text-sm mb-3">
                                Upload insurance-related paperwork separately from your damage photos. PDF · JPG · PNG · HEIC · WebP accepted.
                              </p>
                              <InsuranceDocsUploader items={insuranceFiles} setItems={setInsuranceFiles} />
                            </fieldset>
                          </div>
                        )}

                        {ptype === "custom_jdm_specialty" && (
                          <>
                            <div className="md:col-span-2">
                              <label className="field-label" htmlFor="out">Desired outcome / finish</label>
                              <textarea id="out" rows={3} className="field" value={data.desired_outcome} onChange={set("desired_outcome")} placeholder="Full color change, restoration, drift-panel replacement, spec build…" />
                            </div>
                            <div>
                              <label className="field-label" htmlFor="mods">Prior mods or repairs</label>
                              <input id="mods" className="field" value={data.prior_mods} onChange={set("prior_mods")} />
                            </div>
                            <div>
                              <label className="field-label" htmlFor="parts">Parts already obtained</label>
                              <input id="parts" className="field" value={data.parts_obtained} onChange={set("parts_obtained")} />
                            </div>
                          </>
                        )}

                        <div className="md:col-span-2">
                          <label className="field-label" htmlFor="vlink">Video link (optional)</label>
                          <input id="vlink" className="field" placeholder="YouTube, Vimeo, Google Drive…" value={data.video_link} onChange={set("video_link")} />
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          )}

          {/* STEP 2 — Contact & submit */}
          {step === 2 && (
            <motion.div key="s2" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}
                        transition={{ duration: 0.35 }} className="space-y-8">
              <div className="flex flex-wrap items-center gap-3">
                <span className="inline-flex items-center gap-2 border border-proform-red text-proform-red font-mono text-[11px] tracking-[0.24em] px-3 py-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-proform-red" /> {CATEGORY_BADGE[ptype]}
                </span>
                <button type="button" onClick={changeType} className="font-mono text-[11px] tracking-[0.24em] text-chrome-300 hover:text-proform-red underline underline-offset-4">
                  Change project type
                </button>
              </div>

              <h2 className="font-display uppercase text-3xl md:text-5xl leading-[0.95] tracking-tight">Your contact details.</h2>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="field-label" htmlFor="fn">First name *</label>
                  <input id="fn" required data-testid="estimate-first-name" className="field" value={data.first_name} onChange={set("first_name")} />
                </div>
                <div>
                  <label className="field-label" htmlFor="ln">Last name *</label>
                  <input id="ln" required data-testid="estimate-last-name" className="field" value={data.last_name} onChange={set("last_name")} />
                </div>
                <div>
                  <label className="field-label" htmlFor="ph">Phone *</label>
                  <input id="ph" required type="tel" data-testid="estimate-phone" className="field" value={data.phone} onChange={set("phone")} />
                </div>
                <div>
                  <label className="field-label" htmlFor="em">Email *</label>
                  <input id="em" required type="email" data-testid="estimate-email" className="field" value={data.email} onChange={set("email")} />
                </div>
                <div className="md:col-span-2">
                  <label className="field-label">Preferred contact method</label>
                  <div className="grid grid-cols-3 gap-3">
                    {["phone", "text", "email"].map((m) => (
                      <button key={m} type="button" onClick={() => setData((d) => ({ ...d, preferred_contact: m }))}
                              className={`btn justify-center ${data.preferred_contact === m ? "border-proform-red text-proform-red" : ""}`}>
                        {m.toUpperCase()}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  data-testid={TID.estimate.consent}
                  checked={data.consent}
                  onChange={set("consent")}
                  className="mt-1 h-5 w-5 accent-proform-red"
                />
                <span className="text-chrome-300 text-sm">
                  I agree to be contacted about this inquiry via my preferred method. I understand a final estimate requires in-person inspection and that no promise of final pricing is made from photos alone.
                </span>
              </label>

              {/* ------------------ PRELIMINARY REVIEW ACKNOWLEDGMENT ------------------ */}
              <div className="mt-10 border border-proform-red/60 bg-ink-950 p-5 md:p-6" data-testid="ack-section">
                <div className="label text-proform-red">— Section required</div>
                <h3 className="font-display uppercase text-xl md:text-2xl tracking-tight mt-1">
                  Preliminary Review Acknowledgment
                </h3>
                <p className="text-chrome-300 text-xs mt-1 font-mono tracking-[0.16em] uppercase">
                  Version {ACK_VERSION} · This acknowledgment is required to submit
                </p>

                <div
                  className="mt-4 p-4 md:p-5 border border-ink-700 bg-ink-900 max-h-64 overflow-y-auto text-chrome-300 text-sm leading-relaxed whitespace-pre-line"
                  data-testid="ack-text"
                >
                  {ACK_TEXT}
                </div>

                <label className="mt-5 flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid="ack-checkbox"
                    checked={data.ack_agreed}
                    onChange={set("ack_agreed")}
                    className="mt-1 h-5 w-5 accent-proform-red"
                  />
                  <span className="text-chrome-100 text-sm font-medium">
                    I have read and agree to the Preliminary Review Acknowledgment.
                  </span>
                </label>

                <div className="mt-5 grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="field-label">Full legal name *</label>
                    <input
                      className="field"
                      value={data.ack_legal_name}
                      onChange={set("ack_legal_name")}
                      placeholder="First Middle Last"
                      data-testid="ack-legal-name"
                      autoComplete="name"
                    />
                  </div>
                  <div>
                    <label className="field-label">Date</label>
                    <input
                      className="field"
                      value={new Date().toLocaleDateString()}
                      readOnly
                      data-testid="ack-date"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="field-label">Drawn signature (optional)</label>
                    <SignaturePad
                      onChange={(dataUrl) => setData((d) => ({ ...d, ack_signature: dataUrl }))}
                      testid="ack-signature"
                    />
                  </div>
                </div>

                <p className="mt-4 text-chrome-300 text-xs">
                  For your protection, ProForm records the date, time, IP address, and browser you use to acknowledge this
                  section — not for tracking, but to prove that you agreed to these specific terms if it ever comes up.
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="mt-10 flex items-center justify-between gap-3">
          <button type="button" onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0}
                  className="btn disabled:opacity-40">← Back</button>
          {step < 2 ? (
            <button type="button" onClick={() => canContinue && setStep(step + 1)} disabled={!canContinue}
                    className="btn btn-primary disabled:opacity-40">Continue →</button>
          ) : (
            <button type="submit" disabled={busy || !canContinue} data-testid={TID.estimate.submit}
                    className="btn btn-primary disabled:opacity-40">
              {busy ? "Sending…" : "Acknowledge & Submit →"}
            </button>
          )}
        </div>
      </form>
    </>
  );
}
